import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  isMobileDevice,
  getDeviceType,
  getViewPreference,
  setViewPreference,
  shouldShowMobileView,
  isTouchDevice
} from '../utils/deviceDetection';

const MobileContext = createContext(null);

export const MobileProvider = ({ children }) => {
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const [deviceType, setDeviceType] = useState(getDeviceType());
  const [viewPreference, setViewPref] = useState(getViewPreference());
  const [showMobileView, setShowMobileView] = useState(shouldShowMobileView());
  const [isTouch, setIsTouch] = useState(isTouchDevice());

  // Update device info on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice());
      setDeviceType(getDeviceType());
      setShowMobileView(shouldShowMobileView());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Switch to mobile view
  const switchToMobile = useCallback(() => {
    setViewPreference('mobile');
    setViewPref('mobile');
    setShowMobileView(true);
  }, []);

  // Switch to default view
  const switchToDefault = useCallback(() => {
    setViewPreference('default');
    setViewPref('default');
    setShowMobileView(false);
  }, []);

  // Toggle between views
  const toggleView = useCallback(() => {
    if (showMobileView) {
      switchToDefault();
    } else {
      switchToMobile();
    }
  }, [showMobileView, switchToDefault, switchToMobile]);

  // Reset to auto detection
  const resetToAuto = useCallback(() => {
    localStorage.removeItem('viewPreference');
    setViewPref('auto');
    setShowMobileView(shouldShowMobileView());
  }, []);

  const value = {
    isMobile,
    deviceType,
    viewPreference,
    showMobileView,
    isTouch,
    switchToMobile,
    switchToDefault,
    toggleView,
    resetToAuto
  };

  return (
    <MobileContext.Provider value={value}>
      {children}
    </MobileContext.Provider>
  );
};

export const useMobile = () => {
  const context = useContext(MobileContext);
  if (!context) {
    throw new Error('useMobile must be used within a MobileProvider');
  }
  return context;
};

export default MobileContext;
